import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Order } from "../services";
import { useLanguage } from "../context/LanguageContext";
import { findByName, findVehicleType, validateResolvedRow } from "../utils/bulkOrderHelpers";

const sortCities = (list) =>
  (list || [])
    .filter((city) => city.name)
    .sort((a, b) => a.name.localeCompare(b.name));

// Бэкенду нужно время, чтобы после парсинга Excel дозаписать новые типы ТС
// и категории груза в справочник — иначе запрос вернёт список без них. Ждём перед запросом.
const TYPES_FETCH_DELAY_MS = 1500;
const CATEGORIES_FETCH_DELAY_MS = 1500;
const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Единый источник справочников для модуля массового создания заказов.
 *
 * Кэширует промисы запросов:
 *  - страны и типы транспорта загружаются один раз;
 *  - города кэшируются по countryId (повторяющиеся страны не запрашиваются дважды).
 *
 * Один и тот же инстанс используется и для валидации всех строк на странице,
 * и в модалке редактирования — поэтому повторных запросов не происходит.
 */
export const useBulkDirectories = () => {
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();
  const lang = currentLanguage || "ru";

  const [countries, setCountries] = useState([]);
  const [types, setTypes] = useState([]);
  const [categories, setCategoriesState] = useState([]);

  // Кэш промисов (стабилен между рендерами)
  const countriesPromise = useRef(null);
  const typesPromise = useRef(null);
  const categoriesPromise = useRef(null);
  const citiesPromises = useRef(new Map()); // countryId -> Promise<city[]>

  // Сброс кэша при смене языка
  useEffect(() => {
    countriesPromise.current = null;
    typesPromise.current = null;
    categoriesPromise.current = null;
    citiesPromises.current = new Map();
    setCountries([]);
    setTypes([]);
    setCategoriesState([]);
  }, [lang]);

  const loadCountries = useCallback(() => {
    if (!countriesPromise.current) {
      countriesPromise.current = Order.getCountries(lang)
        .then((res) => res.data || [])
        .then((list) => {
          setCountries(list);
          return list;
        })
        .catch((error) => {
          countriesPromise.current = null;
          throw error;
        });
    }
    return countriesPromise.current;
  }, [lang]);

  const loadTypes = useCallback(() => {
    if (!typesPromise.current) {
      typesPromise.current = delay(TYPES_FETCH_DELAY_MS)
        .then(() => Order.getTypes())
        .then((res) => res.data || [])
        .then((list) => {
          setTypes(list);
          return list;
        })
        .catch((error) => {
          typesPromise.current = null;
          throw error;
        });
    }
    return typesPromise.current;
  }, []);

  // Категории груза запрашиваются один раз. Первый запрос — с задержкой, чтобы
  // бэкенд успел дозаписать категории, добавленные при парсинге Excel.
  const loadCategories = useCallback(() => {
    if (!categoriesPromise.current) {
      categoriesPromise.current = delay(CATEGORIES_FETCH_DELAY_MS)
        .then(() => Order.getCategories(lang))
        .then((res) => res.data || [])
        .then((list) => {
          setCategoriesState(list);
          return list;
        })
        .catch((error) => {
          categoriesPromise.current = null;
          throw error;
        });
    }
    return categoriesPromise.current;
  }, [lang]);

  // Обновление кэша категорий (после добавления новой в AddCategoryModal).
  // Принимает полный новый список — синхронизирует и state, и закэшированный промис.
  const setCategories = useCallback((list) => {
    const next = list || [];
    setCategoriesState(next);
    categoriesPromise.current = Promise.resolve(next);
  }, []);

  const loadCities = useCallback(
    (countryId) => {
      if (!countryId) return Promise.resolve([]);
      const cache = citiesPromises.current;
      if (!cache.has(countryId)) {
        cache.set(
          countryId,
          Order.getCities(lang, countryId)
            .then((res) => sortCities(res.data))
            .catch((error) => {
              cache.delete(countryId);
              throw error;
            }),
        );
      }
      return cache.get(countryId);
    },
    [lang],
  );

  const refreshCities = useCallback(
    (countryId) => {
      citiesPromises.current.delete(countryId);
      return loadCities(countryId);
    },
    [loadCities],
  );

  /**
   * Валидация массива строк: резолвит страну/город/тип из справочников
   * (с переиспользованием кэша) и проставляет служебные поля __errors / __valid
   * и резолвнутые id для дальнейшего создания тендера.
   */
  const validateRows = useCallback(
    async (rows) => {
      const [countryList, typeList] = await Promise.all([loadCountries(), loadTypes()]);

      return Promise.all(
        (rows || []).map(async (row) => {
          const countryFrom = findByName(countryList, row.countryFromName);
          const countryTo = findByName(countryList, row.countryToName);
          // Excel-ответ отдаёт vehicleTypeName (название) и vehicleType (value-код)
          const type = findVehicleType(typeList, row.vehicleTypeName, row.vehicleType);

          // Города запрашиваются параллельно; кэш дедуплицирует одинаковые страны
          const [citiesFrom, citiesTo] = await Promise.all([
            countryFrom ? loadCities(countryFrom.id) : Promise.resolve([]),
            countryTo ? loadCities(countryTo.id) : Promise.resolve([]),
          ]);

          const cityFrom = countryFrom ? findByName(citiesFrom, row.cityFrom) : null;
          const cityTo = countryTo ? findByName(citiesTo, row.cityTo) : null;

          const errors = validateResolvedRow({
            row,
            countryFrom,
            cityFrom,
            countryTo,
            cityTo,
            type,
            t,
          });

          return {
            ...row,
            countryIdFrom: countryFrom?.id ?? null,
            cityIdFrom: cityFrom?.id ?? null,
            countryIdTo: countryTo?.id ?? null,
            cityIdTo: cityTo?.id ?? null,
            // vehicleType — отображаемое название ("Рефрижератор"), vehicleTypeValue — значение API ("refrigerator")
            vehicleType: type?.name ?? row.vehicleTypeName ?? row.vehicleType,
            vehicleTypeValue: type?.value ?? row.vehicleType ?? null,
            __errors: errors,
            __valid: Object.keys(errors).length === 0,
          };
        }),
      );
    },
    [loadCountries, loadTypes, loadCities, t],
  );

  return {
    countries,
    types,
    categories,
    loadCountries,
    loadTypes,
    loadCategories,
    setCategories,
    loadCities,
    refreshCities,
    validateRows,
  };
};
